import AutoLayout from "@src/deprecatedDesignSystem/components/AutoLayout";
import { FC, useCallback, useMemo } from "react";
import { useHrisSearchFilters } from "./hooks/useHrisSearchFilters";
import SingleSelectFilter from "./SingleSelectFilter";
import { gql } from "@apollo/client";
import {
  useHrisLocationFilterOptionsQuery,
  useHrisTeamFilterOptionsQuery,
} from "./HrisFilters.generated";
import MultiSelectDropdown from "@src/deprecatedDesignSystem/components/SelectDropdowns/MultiSelectDropdown";
import { pluralize } from "@src/utils/strings";
import { StyleSheet } from "aphrodite";
import { deprecatedTones } from "@src/deprecatedDesignSystem/styles/deprecatedColors";

type Props = {
  showInviteFilter?: boolean;
};
const HrisFilters: FC<Props> = ({ showInviteFilter = true }) => {
  const { hrisSearchFilters, setHrisSearchFilters } = useHrisSearchFilters();
  const { data: hrisLocations } = useHrisLocationFilterOptionsQuery({
    fetchPolicy: "no-cache",
    variables: {
      input: {
        search: undefined,
        sort: undefined,
      },
    },
  });
  const { data: hrisRoles } = useHrisTeamFilterOptionsQuery({
    fetchPolicy: "no-cache",
    variables: {
      input: {
        search: undefined,
        sort: undefined,
      },
    },
  });

  const hrisLocationOptions = useMemo(() => {
    return hrisLocations?.HrisLocations?.objects.map((location) => {
      return {
        label: location.name,
        value: location.id,
      };
    });
  }, [hrisLocations?.HrisLocations?.objects]);
  const hrisRoleOptions = useMemo(() => {
    return hrisRoles?.HrisTeams?.objects.map((role) => {
      return {
        label: role.name,
        value: role.id,
      };
    });
  }, [hrisRoles?.HrisTeams?.objects]);

  const onLocationsChange = useCallback(
    (newLocationIds: number[]) => {
      setHrisSearchFilters({
        ...hrisSearchFilters,
        locationIds: newLocationIds,
      });
    },
    [setHrisSearchFilters, hrisSearchFilters],
  );
  const onRolesChange = useCallback(
    (newRoleIds: number[]) => {
      setHrisSearchFilters({
        ...hrisSearchFilters,
        roleIds: newRoleIds,
      });
    },
    [setHrisSearchFilters, hrisSearchFilters],
  );
  const onInviteStatusChange = useCallback(
    (newInviteStatus: boolean | undefined) => {
      setHrisSearchFilters({
        ...hrisSearchFilters,
        inviteStatus: newInviteStatus,
      });
    },
    [setHrisSearchFilters, hrisSearchFilters],
  );

  return (
    <AutoLayout
      direction={"horizontal"}
      alignmentVertical={"center"}
      spaceBetweenItems={8}
      marginBottom={3}
    >
      {showInviteFilter && (
        <SingleSelectFilter
          title="Invite Status"
          selected={hrisSearchFilters.inviteStatus}
          options={[
            { label: "Invited", value: true },
            { label: "Not Invited", value: false },
          ]}
          onChange={onInviteStatusChange}
        />
      )}
      <MultiSelectDropdown
        values={hrisSearchFilters.locationIds}
        onChange={onLocationsChange}
        label="Locations"
        options={hrisLocationOptions ? hrisLocationOptions : []}
        popoverOpenStyleDeclaration={styles.popoverOpen}
        getSelectedCopy={(options) =>
          `${options.length} ${pluralize("Location", options.length)}`
        }
        enableSearch
      />
      <MultiSelectDropdown
        values={hrisSearchFilters.roleIds}
        onChange={onRolesChange}
        label="Roles"
        options={hrisRoleOptions ? hrisRoleOptions : []}
        popoverOpenStyleDeclaration={styles.popoverOpen}
        getSelectedCopy={(options) =>
          `${options.length} ${pluralize("Role", options.length)}`
        }
        enableSearch
      />
    </AutoLayout>
  );
};

const styles = StyleSheet.create({
  popoverOpen: {
    border: `1px solid ${deprecatedTones.blue5}`,
    boxShadow: `0px 0px 0px 3px ${deprecatedTones.blue3}`,
  },
});

gql`
  query HrisLocationFilterOptions(
    $input: HrisLocationsInput!
    $pagination: PaginationInput
  ) {
    HrisLocations(input: $input, pagination: $pagination) {
      objects {
        ...HrisLocationFilterOption
      }
    }
  }
  query HrisTeamFilterOptions(
    $input: HrisTeamsInput!
    $pagination: PaginationInput
  ) {
    HrisTeams(input: $input, pagination: $pagination) {
      objects {
        ...HrisTeamFilterOption
      }
    }
  }
  fragment HrisLocationFilterOption on HrisLocation {
    id
    name
  }
  fragment HrisTeamFilterOption on HrisTeam {
    id
    name
  }
`;

export default HrisFilters;
